/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */
 /* ------------------------------------------------------------------------------------------------------------
* Generic reveal on scroll handler
* ------------------------------------------------------------------------------------------------------------
*/
(function() {
  const initRevealOnScroll = () => {
    const revealOnScrollElems = document.querySelectorAll('[data-reveal-on-scroll]:not([data-reveal-on-scroll=""]');

    const REVEAL_CLASS = 'reveal-fade-up';
    let counter = revealOnScrollElems.length;
    const callback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const revealClass = entry.target.dataset.revealOnScroll || REVEAL_CLASS;
          if (!entry.target.classList.contains(revealClass)) {
            entry.target.classList.add(revealClass);
            observer.unobserve(entry.target);
            counter--;

            if (!counter) {
              observer.disconnect();
            }
          }
        }
      })
    }
    const revealObserver = new IntersectionObserver(callback);

    revealOnScrollElems.forEach((elem) => {
      revealObserver.observe(elem);
    });

    return revealObserver;
  };
  const disconnectObserver = (observer) => {
    if (observer && observer.disconnect) {
      observer.disconnect();
    }
  };
  const onShopifySectionEvent = (observer) => {
    disconnectObserver(observer);
    mainObserver = initRevealOnScroll();
  };
  let mainObserver = {};

  document.addEventListener('DOMContentLoaded', () => {
    mainObserver = initRevealOnScroll();
  });
  document.addEventListener('shopify:section:unload', () => {
    onShopifySectionEvent(mainObserver);
  });
  document.addEventListener('shopify:section:load', () => {
    onShopifySectionEvent(mainObserver);
  });
})();

/* ------------------------------------------------------------------------------------------------------------
* Klaviyo Added to Cart Tracking
* ------------------------------------------------------------------------------------------------------------
*/
(function() {
  document.addEventListener('variant:added', function(event) {
    const detail = event && event.detail ? event.detail : {};
    const src = detail.src;
    const cart = detail.cart;
    const cartVariant = detail.variant;
    let cartData = {};

    // Klaviyo created variables
    let addedItem = window.item;
    let _learnq = window._learnq || [];

    // Update Klaviyo created item price. Klaviyo injected snippet uses product
    // price which may not be the actual price of the added varaint.
    if (addedItem && cartVariant) {
      addedItem.Price = window.RS.helpers.formatMoney(cartVariant.price);
    }

    // If not on the pdp or is the main product form on the pdp and the Klaviyo
    // item object does not exist, mimic Klaviyo item object as best as possible.
    // Some properties are not available from JS Shopify object.
    if (src !== 'pdp' || ( src === 'pdp' && !addedItem)) {
      if (cartVariant) {
        addedItem = {
          Brand: cartVariant.vendor,
          ImageURL: cartVariant.image,
          Name: cartVariant.product_title,
          Price: window.RS.helpers.formatMoney(cartVariant.price),
          ProductID: cartVariant.product_id,
          URL: `${window.themeVariables.routes.secureShopUrl}${cartVariant.url}`
        }
      }
    }

    if (addedItem) {
      addedItem.src = src;
    }

    if (cart) {
      // Format from Klaviyo docs:
      // https://help.klaviyo.com/hc/en-us/articles/115001396711-Guide-to-Creating-an-Added-to-Cart-Event-for-Shopify
      cartData = {
        total_price: cart.total_price/100,
        $value: cart.total_price/100,
        total_discount: cart.total_discount,
        original_total_price: cart.original_total_price/100,
        items: cart.items
      }
    }

    _learnq.push([
      'track',
      'Added to Cart',
      {
        ...(addedItem || {}),
        ...cartData
      }
    ])
  });
})();

/*
 * LEGACY/MIXED -- STAYAI CART
 */
window.RS = window.RS || {};
RS.Cart = (function() {
  const CART_DRAWER_EL = 'cart-drawer';
  const RECOMMENDATIONS_EL = 'cart-drawer-recommendations';
  const PRODUCT_ITEM = 'product-item';
  const PROD_ANCHOR_EL = '.product-item__image-wrapper a';
  const DRAWER_EL = 'quick-buy-drawer.drawer';
  const POPOVER_EL = 'quick-buy-popover.popover';
  const TOGGLE_EL = 'button[is="toggle-button"]';
  const ATC_EL = '.product-item__quick-form button[type="submit"]';
  const QB_El = '.product-item__quick-buy-button';
  const SHOP_FORM_EL = '.shopify-product-form';
  const SUB_EL = 'input[value="purchaseTypeSubscription"]';
  const ONE_TIME_EL = 'input[value="purchaseTypeOneTime"]';
  const CART_REFRESH = new CustomEvent('cart:refresh', {
      bubbles: true
  });
  const CART_SHOW = new CustomEvent('cart-notification:show', {
    bubbles: true,
    cancelable: true,
    detail: {
      status: "success"
    }
  });

  const refreshCart = function() {
    // cart recommendations is triggered by this event
    document.documentElement.dispatchEvent(CART_REFRESH);
  }
  const onAddedToCart = function(delay = false, openCart = true) {
    var cartType = window.themeVariables.settings.cartType;
    switch ( cartType ) {
      case 'drawer':
        var delayTS = delay ? 500 : 0;
        refreshCart();
        setTimeout(function () {
          window.lsgRenderCart();
          if ( openCart ) {
            document.getElementById('mini-cart').open = true;
          }
        }, delayTS);
      break;
      case 'message':
        document.documentElement.dispatchEvent(CART_SHOW);
      break;
      case 'page':
        window.location.href = `${Shopify.routes.root}cart`;
      break;
    }
  }
  const quickAdd = function(event, parent, openCart = true) {
    const target = event.currentTarget
    if ( !target) return;
    let parentEl;
    if ( parent ) {
      parentEl = parent;
    } else {
      parentEl = target.closest(PRODUCT_ITEM);
    }
    const drawerEl = parentEl?.querySelector(DRAWER_EL);
    if ( drawerEl?.open ) {
      drawerEl.open = false;
    }
    const popoverEl = parentEl?.querySelector(POPOVER_EL);
    if ( popoverEl?.open ) {
      popoverEl.open = false;
    }

    addToCart(event, openCart);
  }
  const addToCart = function(event, openCart = true) {
    if ( !event ) return;
    event.preventDefault();
    /*
      Stop Focal loader button from calling form.submit()
    */
    event.stopImmediatePropagation();
    const target = event.currentTarget;
    if ( target ) {
      target.setAttribute('aria-busy', "true");
    }

    const formEl = target?.closest(SHOP_FORM_EL);
    const subEL = formEl?.querySelector(SUB_EL);
    const oneTimeEl = formEl?.querySelector(ONE_TIME_EL);

    let variantId = formEl?.querySelector('input[name="id"]')?.value;
    const qty = formEl?.querySelector('[name="quantity"]')?.value;
    const prodId = formEl.querySelector('input[data-productid]')?.dataset.productid;
    const spDropdownEl = document.querySelector(`select.rtx-subscription-dropdown-${prodId}[name='selling_plan']`);
    let itemProperties = null;
    if(document.querySelector("textarea#custom-case-breakdown")) {
      itemProperties = {
        "Custom Case Breakdown": document.querySelector("textarea#custom-case-breakdown").value
      }
    }

    // STAYAI Selling plan
    let selling_plan_id;

    if ( subEL?.checked ) {
      selling_plan_id = spDropdownEl?.value;
    }

    if (!variantId || variantId == ""){
      variantId = formEl?.querySelector(".product-option.active")?.dataset.id;
    }
    try {
      CartJS.addItems([{
        id: variantId,
        quantity: qty,
        selling_plan: selling_plan_id,
        properties: itemProperties,
        success: setTimeout(async function () {
          target.removeAttribute("aria-busy");
          await onAddedToCart(true, openCart);
          if(window.saq) {
            saq('ts','5VayyWz0gqKG_sf_HBgTAw', { event: 'addtocart' });
          }
        }, 1000)
      }]);
    } catch (err) {
      console.error(err);
    }
  }
  return {
    ATC_EL,
    CART_DRAWER_EL,
    PROD_ANCHOR_EL,
    PRODUCT_ITEM,
    QB_El,
    TOGGLE_EL,
    onAddedToCart,
    quickAdd,
    addToCart
  }
})();

/*
 * LEGACY/MIXED -- Add to Cart event callbacks
 */
  document.addEventListener("DOMContentLoaded", () => {
    const atcEls = document.querySelectorAll(RS.Cart.ATC_EL);
    // ATC
    for (let i = 0; i < atcEls.length; i++) {
      const el = atcEls[i];
      if ( el ) {
        el.addEventListener('click', RS.Cart.addToCart);
      }
    }
    const _anchor = document.querySelector('a[href="/cart"].header__icon-wrapper');
    const cartType = window.themeVariables.settings.cartType;
    if ( _anchor && cartType !== 'drawer') {
      _anchor.addEventListener('click', function(e) {
        if ( e.currentTarget.href ) {
          window.location.href = e.currentTarget.href;
        }
      });
    }
  });

  /* enforce $50 cart min ajax */
  document.addEventListener('cart:updated', function(event) {
    const DISABLED = 'disabled';
    let cart = event.detail.cart; // Get the updated content of the cart
    let checkoutBtn = document.querySelector('.checkout-button');
    let cartMinNotice = document.querySelector('.cart-min-notice');

    if (cart) {
      if (cart.total_price < 5000) {
        if (!checkoutBtn.classList.contains(DISABLED)) {
          checkoutBtn.classList.add(DISABLED)
          cartMinNotice.classList.remove('hidden')
        }
      } else {
        if (checkoutBtn.classList.contains(DISABLED)) {
          checkoutBtn.classList.remove(DISABLED)
          cartMinNotice.classList.add('hidden')
        }
      }
    }
   });